/**
 * Self-registration for marketing employees, managers and VPs.
 *
 * Submitting does not create a login — it creates a request an administrator
 * approves. The page says so before the form, not after submitting, because a
 * person who expects to sign in straight away and cannot will assume it is
 * broken and register again.
 *
 * The employee id is the whole point of the form: it is what ties the account
 * to one person's place in the hierarchy, and therefore what limits which
 * team's revenue they can see.
 */

import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AlertCircle, ArrowLeft, CheckCircle2, ShieldCheck } from "lucide-react";

import { api, fieldErrors } from "@/api/client";
import { EmployeeIdField } from "@/features/admin/EmployeeIdField";
import { ROLE_LABEL, type EmployeeIdCheck, type Role } from "@/types/api";

const SELF_SERVE_ROLES: Role[] = ["EMPLOYEE", "MANAGER", "VP"];

export function Register() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    username: "", full_name: "", email: "", employee_id: "",
    requested_role: "EMPLOYEE" as Role, password: "", confirm: "",
  });
  const [check, setCheck] = useState<EmployeeIdCheck | null>(null);
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[key as string];
      delete next.__all__;
      return next;
    });
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (form.password !== form.confirm) {
      setErrors({ confirm: ["The two passwords do not match."] });
      return;
    }
    setBusy(true);
    setErrors({});
    try {
      await api.post("/auth/register", {
        username: form.username.trim(),
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        employee_id: form.employee_id.trim(),
        requested_role: form.requested_role,
        password: form.password,
      });
      setDone(true);
    } catch (err) {
      const found = fieldErrors(err);
      setErrors(Object.keys(found).length
        ? found
        : { __all__: ["Could not submit the request. Try again shortly."] });
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <Centered>
        <div className="card p-6 space-y-3 text-center">
          <CheckCircle2 size={26} className="mx-auto text-good" />
          <h1 className="font-display font-bold text-[19px] tracking-tight">
            Request sent for approval
          </h1>
          <p className="text-[13.5px] text-ink-soft">
            An administrator has to approve <strong>{form.username}</strong>
            {" "}before you can sign in. This system carries revenue and payout
            figures, so accounts are never created automatically.
          </p>
          <p className="text-[12.5px] text-ink-faint">
            Your password is already saved — you will not be asked to choose
            another one.
          </p>
          <button onClick={() => navigate("/login")}
                  className="btn btn-primary w-full justify-center">
            Back to sign in
          </button>
        </div>
      </Centered>
    );
  }

  return (
    <Centered>
      <form onSubmit={submit} className="card p-6 space-y-4">
        <div>
          <div className="label">Marketing Command Center</div>
          <h1 className="font-display font-bold text-[22px] tracking-tight">
            Request access
          </h1>
        </div>

        <div className="flex items-start gap-2 rounded-md border border-rule
                        bg-surface-alt px-3 py-2.5">
          <ShieldCheck size={14} className="mt-0.5 shrink-0 text-petrol" />
          <p className="text-[12.5px] text-ink-soft">
            This form does <strong>not</strong> create a login. An administrator
            checks your employee id against the marketing hierarchy and approves
            it — usually the same working day.
          </p>
        </div>

        <Field label="Full name" required errors={errors.full_name}>
          <input
            value={form.full_name} onChange={(e) => set("full_name", e.target.value)}
            required autoComplete="name" className={inputClass(errors.full_name)}
          />
        </Field>

        <EmployeeIdField
          value={form.employee_id}
          onChange={(v) => set("employee_id", v)}
          onResolved={setCheck}
          required
          errors={errors.employee_id}
          hint="From your payslip or ID card — MR-EMP-0042, WL-NXP-70615 or HR-EMP-00123. Any of your ids works."
        />

        <Field label="Role you need" errors={errors.requested_role}>
          <select
            value={form.requested_role}
            onChange={(e) => set("requested_role", e.target.value as Role)}
            className={inputClass(errors.requested_role)}
          >
            {SELF_SERVE_ROLES.map((role) => (
              <option key={role} value={role}>{ROLE_LABEL[role]}</option>
            ))}
          </select>
          <p className="text-[11.5px] text-ink-faint mt-1">
            The approver can grant a narrower role than you ask for.
            Administrator rights are never granted from this form.
          </p>
        </Field>

        <Field label="Username" required errors={errors.username}>
          <input
            value={form.username} onChange={(e) => set("username", e.target.value)}
            required autoComplete="username" spellCheck={false}
            className={inputClass(errors.username)}
          />
        </Field>

        <Field label="Work email" errors={errors.email}>
          <input
            type="email" value={form.email}
            onChange={(e) => set("email", e.target.value)}
            autoComplete="email" className={inputClass(errors.email)}
          />
        </Field>

        <Field label="Password" required errors={errors.password}>
          <input
            type="password" value={form.password}
            onChange={(e) => set("password", e.target.value)}
            required autoComplete="new-password"
            className={inputClass(errors.password)}
          />
          <p className="text-[11.5px] text-ink-faint mt-1">
            At least 12 characters, and not a common password.
          </p>
        </Field>

        <Field label="Confirm password" required errors={errors.confirm}>
          <input
            type="password" value={form.confirm}
            onChange={(e) => set("confirm", e.target.value)}
            required autoComplete="new-password"
            className={inputClass(errors.confirm)}
          />
        </Field>

        {errors.__all__?.map((message) => (
          <p key={message} className="text-[13px] text-bad bg-bad-soft border
                                      border-bad/25 rounded-md px-3 py-2
                                      flex items-start gap-2">
            <AlertCircle size={14} className="mt-0.5 shrink-0" />
            <span>{message}</span>
          </p>
        ))}

        <button
          type="submit"
          disabled={busy || !form.employee_id.trim() || check?.ok === false}
          className="btn btn-primary w-full justify-center disabled:opacity-60"
        >
          {busy ? "Sending…" : "Send request"}
        </button>

        <Link to="/login"
              className="flex items-center justify-center gap-1.5 text-[12.5px]
                         text-petrol hover:underline">
          <ArrowLeft size={12} /> I already have an account
        </Link>
      </form>
    </Centered>
  );
}

// ---------------------------------------------------------------------------
function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen grid place-items-center p-6">
      <div className="w-full max-w-md">{children}</div>
    </div>
  );
}

export function inputClass(errors?: string[]) {
  return `w-full text-sm bg-surface border rounded-md px-2.5 py-2
          focus:outline-none focus:ring-2 focus:ring-petrol/30
          ${errors?.length ? "border-bad" : "border-rule"}`;
}

export function Field({
  label, required, errors, children,
}: {
  label: string; required?: boolean; errors?: string[];
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1">
      <label className="block space-y-1">
        <span className="label">
          {label}{required && <span className="text-bad"> *</span>}
        </span>
        {children}
      </label>
      {errors?.map((message) => (
        <p key={message} className="text-[12px] text-bad">{message}</p>
      ))}
    </div>
  );
}
